import React, { useState, useEffect } from "react";
import { BarChart3, AlertCircle, TrendingUp, FileText, ShoppingCart, Briefcase, ClipboardList } from "lucide-react";
import { Card, Table, Spinner, ErrBox } from "../components/UI";
import { MiniBarChart, StatCard } from "../components/Charts";
import Badge from "../components/Badge";
import { fmtRp, fmtDate } from "../utils/format";
import { dashboardAPI } from "../utils/api";

const ModuleKpiCard = ({ title, icon, color, items }) => (
  <Card style={{ borderTop: `3px solid ${color}`, padding: "14px 16px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
      <span style={{ color }}>{icon}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{title}</span>
    </div>
    {items.map(([label, val, valColor]) => (
      <div key={label} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 12 }}>
        <span style={{ color: "#6B7280" }}>{label}</span>
        <span style={{ fontWeight: 700, fontFamily: "monospace", color: valColor || "#374151" }}>{val}</span>
      </div>
    ))}
  </Card>
);

const DashboardPage = () => {
  const thisYear = new Date().getFullYear();
  const [tahun, setTahun]       = useState(String(thisYear));
  const [dari, setDari]         = useState("");
  const [sampai, setSampai]     = useState("");
  const [summary, setSummary]   = useState(null);
  const [moduleKpi, setModuleKpi] = useState(null);
  const [chart, setChart]       = useState([]);
  const [trx, setTrx]           = useState([]);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState("");

  const load = async (t = tahun) => {
    setLoading(true); setErr("");
    try {
      const [s, c, tr, mk] = await Promise.all([
        dashboardAPI.summary(t),
        dashboardAPI.chartSerapan(t),
        dashboardAPI.transaksiTerbaru(20),
        dashboardAPI.moduleKpi(t).catch(() => ({ data: null })),
      ]);
      setSummary(s.data);
      setChart(c.data || []);
      setTrx(tr.data || []);
      setModuleKpi(mk.data);
    } catch(e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(tahun); }, [tahun]);

  const filteredTrx = trx.filter(t => {
    if (!t.tanggal) return true;
    const d = t.tanggal.slice(0, 10);
    if (dari   && d < dari)   return false;
    if (sampai && d > sampai) return false;
    return true;
  });

  const tahunOpts = Array.from({ length: 8 }, (_, i) => thisYear - i);
  const inputStyle = {
    padding: "8px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8,
    fontSize: 13, outline: "none", background: "white", fontFamily: "inherit", color: "#374151",
  };
  const labelStyle = {
    display: "block", fontSize: 11, fontWeight: 700, color: "#64748B",
    textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5,
  };

  const d = summary;
  return (
    <div>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:700, color:"#111827", margin:0 }}>Executive Dashboard</h1>
          <p style={{ color:"#6B7280", fontSize:13, margin:"4px 0 0" }}>Ringkasan kondisi keuangan organisasi</p>
        </div>
        <div style={{
          display:"flex", alignItems:"flex-end", gap:12, flexWrap:"wrap",
          background:"linear-gradient(135deg,#F8FAFC,#F1F5F9)",
          border:"1.5px solid #E2E8F0", borderRadius:12, padding:"14px 18px",
        }}>
          <div>
            <label style={labelStyle}>Tahun Anggaran</label>
            <select value={tahun} onChange={e=>setTahun(e.target.value)}
              style={{ ...inputStyle, minWidth:110, fontWeight:700, cursor:"pointer" }}>
              {tahunOpts.map(y=><option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Mulai Tanggal</label>
            <input type="date" value={dari} onChange={e=>setDari(e.target.value)}
              style={{ ...inputStyle, minWidth:148 }}
              onFocus={e=>e.target.style.borderColor="#2563EB"}
              onBlur={e=>e.target.style.borderColor="#E2E8F0"}/>
          </div>
          <div>
            <label style={labelStyle}>Sampai Tanggal</label>
            <input type="date" value={sampai} onChange={e=>setSampai(e.target.value)}
              style={{ ...inputStyle, minWidth:148 }}
              onFocus={e=>e.target.style.borderColor="#2563EB"}
              onBlur={e=>e.target.style.borderColor="#E2E8F0"}/>
          </div>
          {(dari || sampai) && (
            <button onClick={()=>{setDari("");setSampai("");}}
              style={{ ...inputStyle, background:"#FEF2F2", border:"1.5px solid #FECACA",
                color:"#DC2626", fontWeight:600, cursor:"pointer", alignSelf:"flex-end" }}>
              Reset
            </button>
          )}
        </div>
      </div>

      {err && <div style={{marginBottom:16}}><ErrBox msg={err} onRetry={()=>load()}/></div>}
      {loading ? <Spinner/> : d && (
        <>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(210px,1fr))", gap:16, marginBottom:24 }}>
            <StatCard icon={<BarChart3/>}   label="Total Posisi Kas"      color="#2563EB"
              value={fmtRp(d.posisi_kas?.saldo_total ?? 0)}
              sub={`Masuk ${fmtRp(d.posisi_kas?.total_masuk ?? 0)}`}/>
            <StatCard icon={<BarChart3/>}   label={`Serapan ${tahun}`}    color="#7C3AED"
              value={`${d.anggaran?.persentase_serapan ?? 0}%`}
              sub={`Realisasi ${fmtRp(d.anggaran?.realized ?? 0)} / Pagu ${fmtRp(d.anggaran?.total_pagu ?? 0)}`}/>
            <StatCard icon={<AlertCircle/>} label="Pending Approval"      color="#D97706"
              value={`${d.payment_request?.pending ?? 0} SPP`}
              sub={`Nilai ${fmtRp(d.payment_request?.total_nilai_pending ?? 0)}`}/>
            <StatCard icon={<TrendingUp/>}  label="Penerimaan Bulan Ini"  color="#059669"
              value={fmtRp(d.penerimaan_bulan_ini ?? 0)}
              sub={`Keluar ${fmtRp(d.pengeluaran_bulan_ini ?? 0)}`}/>
          </div>
          {moduleKpi && (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12, marginBottom:24 }}>
              <ModuleKpiCard title="RENDAL" icon={<BarChart3 size={16}/>} color="#2563EB" items={[
                ["Draft", moduleKpi.rendal?.draft || 0, "#D97706"],
                ["Aktif", moduleKpi.rendal?.aktif || 0, "#059669"],
                ["Revisi", moduleKpi.rendal?.revisi || 0, "#DC2626"],
              ]}/>
              <ModuleKpiCard title="PROGAR" icon={<FileText size={16}/>} color="#7C3AED" items={[
                ["Menunggu SIRUP", moduleKpi.progar?.menunggu_sirup || 0, "#D97706"],
                ["Proses Pengadaan", moduleKpi.progar?.proses || 0, "#2563EB"],
                ["Total", moduleKpi.progar?.total || 0],
              ]}/>
              <ModuleKpiCard title="BALAKADA" icon={<ShoppingCart size={16}/>} color="#D97706" items={[
                ["Pending", moduleKpi.balakada?.pending || 0, "#D97706"],
                ["Tender", moduleKpi.balakada?.tender || 0, "#DC2626"],
                ["Langsung", moduleKpi.balakada?.langsung || 0, "#059669"],
              ]}/>
              <ModuleKpiCard title="PPK" icon={<Briefcase size={16}/>} color="#059669" items={[
                ["Menunggu Kontrak", moduleKpi.ppk?.menunggu || 0, "#D97706"],
                ["Aktif", moduleKpi.ppk?.aktif || 0, "#059669"],
                ["Pelaksanaan", moduleKpi.ppk?.pelaksanaan || 0, "#2563EB"],
              ]}/>
              <ModuleKpiCard title="FINANCE" icon={<ClipboardList size={16}/>} color="#DC2626" items={[
                ["Verifikasi", moduleKpi.finance?.verifikasi || 0, "#D97706"],
                ["Approval", moduleKpi.finance?.approval || 0, "#7C3AED"],
                ["Selesai", moduleKpi.finance?.selesai || 0, "#059669"],
              ]}/>
            </div>
          )}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:24 }}>
            <Card>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                <h3 style={{ margin:0, fontSize:14, fontWeight:700 }}>Serapan Bulanan {tahun}</h3>
                <div style={{ display:"flex", gap:12 }}>
                  {[["#DBEAFE","Pagu"],["#2563EB","Realisasi"]].map(([bg,l]) => (
                    <div key={l} style={{ display:"flex", alignItems:"center", gap:4, fontSize:11, color:"#6B7280" }}>
                      <div style={{ width:10, height:10, borderRadius:2, background:bg }}/> {l}
                    </div>
                  ))}
                </div>
              </div>
              {chart.length > 0 ? <MiniBarChart data={chart}/> :
                <p style={{color:"#9CA3AF",fontSize:13,textAlign:"center",padding:24}}>Belum ada data</p>}
            </Card>
            <Card>
              <h3 style={{ margin:"0 0 16px", fontSize:14, fontWeight:700 }}>Ringkasan Anggaran {tahun}</h3>
              {[
                ["Total Pagu",  d.anggaran?.total_pagu ?? 0,      "#2563EB"],
                ["Realisasi",   d.anggaran?.realized ?? 0,       "#10B981"],
                ["Sisa Pagu",   d.anggaran?.sisa_pagu ?? 0,       "#F59E0B"],
                ["Penerimaan",  d.penerimaan_bulan_ini ?? 0,      "#059669"],
                ["Pengeluaran", d.pengeluaran_bulan_ini ?? 0,     "#DC2626"],
              ].map(([l, v, c]) => (
                <div key={l} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:11 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:c, flexShrink:0 }}/>
                    <span style={{ fontSize:13, color:"#374151" }}>{l}</span>
                  </div>
                  <span style={{ fontSize:13, fontWeight:700, color:c, fontFamily:"monospace" }}>{fmtRp(v)}</span>
                </div>
              ))}
              <div style={{ marginTop:16, paddingTop:14, borderTop:"1px solid #F1F5F9" }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                  <span style={{ fontSize:11, color:"#6B7280", fontWeight:600 }}>Tingkat Serapan</span>
                  <span style={{ fontSize:11, fontWeight:700, color: (d.anggaran?.persentase_serapan ?? 0)>80?"#EF4444":(d.anggaran?.persentase_serapan ?? 0)>60?"#F59E0B":"#2563EB" }}>
                    {d.anggaran?.persentase_serapan ?? 0}%
                  </span>
                </div>
                <div style={{ height:8, background:"#F1F5F9", borderRadius:4 }}>
                  <div style={{ height:"100%", borderRadius:4, transition:"width 0.6s ease",
                    width:`${Math.min(d.anggaran?.persentase_serapan ?? 0,100)}%`,
                    background: (d.anggaran?.persentase_serapan ?? 0)>80?"#EF4444":(d.anggaran?.persentase_serapan ?? 0)>60?"#F59E0B":"#2563EB"
                  }}/>
                </div>
              </div>
            </Card>
          </div>
          <Card>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <h3 style={{ margin:0, fontSize:14, fontWeight:700 }}>Transaksi Terbaru</h3>
              {(dari || sampai) && (
                <span style={{ fontSize:12, color:"#6B7280", background:"#F1F5F9", padding:"4px 10px", borderRadius:20 }}>
                  {dari && sampai ? `${fmtDate(dari)} - ${fmtDate(sampai)}`
                    : dari   ? `Dari ${fmtDate(dari)}`
                    : `Sampai ${fmtDate(sampai)}`}
                  <span style={{ marginLeft:6, color:"#9CA3AF" }}>({filteredTrx.length} transaksi)</span>
                </span>
              )}
            </div>
            <Table pageSize={5} cols={[
              { key:"nomor",      label:"Nomor", nowrap:true },
              { key:"tanggal",    label:"Tanggal", render:v=>fmtDate(v), nowrap:true },
              { key:"keterangan", label:"Keterangan" },
              { key:"tipe",       label:"Tipe",   render:v=><Badge status={v}/> },
              { key:"jumlah",     label:"Jumlah", align:"right",
                render:v=><span style={{fontWeight:600,fontFamily:"monospace"}}>{fmtRp(v)}</span>, nowrap:true },
              { key:"status",     label:"Status", render:v=><Badge status={v}/> },
            ]} data={filteredTrx}/>
          </Card>
        </>
      )}
    </div>
  );
};

export default DashboardPage;
